/**
 * Student Detail Page
 * View and manage individual student
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNotification } from '@/hooks/useNotification';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { apiRequest } from '@/api/client';
import { generateCredentialHTML, downloadPDF, printHTML } from '@/utils/credentialDocument';

interface Student {
  studentId: string;
  firstName: string;
  lastName: string;
  username: string;
  password?: string;
  classes: string[];
  storiesCreated?: number;
  weeklyStories?: number;
  lastActive?: string;
  parentEmail?: string;
  parentName?: string;
}

export const StudentDetailPage = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [student, setStudent] = useState<Student | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (studentId) {
      fetchStudent();
    }
  }, [studentId]);

  const fetchStudent = async () => {
    try {
      setIsLoading(true);
      
      const response = await apiRequest<{ student: Student }>('get_student_by_id', {
        studentId,
        teacherEmail: user?.email,
      });
      
      if (import.meta.env.DEV) {
        console.log('Student response:', response);
      }
      
      if (response.success && (response as any).student) {
        const studentData = (response as any).student;
        // Removed sensitive console.log that was exposing student passwords
        setStudent(studentData);
      }
    } catch (error) {
      console.error('Failed to fetch student:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadCredentials = async () => {
    if (!student) return;

    // Get school settings from localStorage
    const savedSettings = localStorage.getItem('schoolSettings');
    const schoolSettings = savedSettings ? JSON.parse(savedSettings) : {
      schoolName: user?.schoolName || 'School',
      schoolLogo: '',
      schoolAddress: '',
      schoolPhone: '',
      schoolEmail: '',
    };

    const html = await generateCredentialHTML({
      student: {
        firstName: student.firstName,
        lastName: student.lastName,
        username: student.username,
        password: student.password || 'Not available',
        gradeName: student.classes?.[0] || undefined,
      },
      teacherName: `${user?.firstName} ${user?.lastName}`,
      schoolSettings,
    });

    await downloadPDF(html, `${student.firstName}_${student.lastName}_credentials.pdf`);
    showNotification('success', 'Credentials downloaded');
  };

  const handlePrintCredentials = async () => {
    if (!student) return;

    const savedSettings = localStorage.getItem('schoolSettings');
    const schoolSettings = savedSettings ? JSON.parse(savedSettings) : {
      schoolName: user?.schoolName || 'School',
      schoolLogo: '',
      schoolAddress: '',
      schoolPhone: '',
      schoolEmail: '',
    };

    const html = await generateCredentialHTML({
      student: {
        firstName: student.firstName,
        lastName: student.lastName,
        username: student.username,
        password: student.password || 'Not available',
        gradeName: student.classes?.[0] || undefined,
      },
      teacherName: `${user?.firstName} ${user?.lastName}`,
      schoolSettings,
    });

    printHTML(html);
  };

  const handleDisconnectParent = async () => {
    if (!student || !student.parentEmail) return;

    const confirmed = window.confirm(
      `Are you sure you want to disconnect ${student.parentName || student.parentEmail} from ${student.firstName}'s account?\n\nThe parent will no longer be able to view ${student.firstName}'s stories and progress.`
    );

    if (!confirmed) return;

    setIsDisconnecting(true);
    try {
      const response = await apiRequest('disconnect_parent', {
        teacherEmail: user?.email,
        studentId: student.studentId,
      });

      if (response.success) {
        showNotification('success', 'Parent account disconnected successfully');
        // Refresh student data
        fetchStudent();
      } else {
        showNotification('error', (response as any).error || 'Failed to disconnect parent');
      }
    } catch (error) {
      console.error('Failed to disconnect parent:', error);
      showNotification('error', 'Failed to disconnect parent account');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleDeleteStudent = async () => {
    try {
      setIsDeleting(true);
      
      const response = await apiRequest('delete_student', {
        teacherEmail: user?.email,
        studentId: studentId,
      });

      if (response.success) {
        showNotification('success', 'Student deleted successfully');
        navigate('/students');
      } else {
        showNotification('error', response.error || 'Failed to delete student');
      }
    } catch (error) {
      console.error('Failed to delete student:', error);
      showNotification('error', 'Failed to delete student');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="space-y-6">
        <Card>
          <div className="p-12 text-center">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Student not found
            </h3>
            <p className="text-slate-600 mb-6">
              This student may have been deleted or you don't have permission to view them.
            </p>
            <Button variant="primary" onClick={() => navigate('/students')}>
              Back to Students
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {student.firstName} {student.lastName}
          </h1>
          <p className="text-slate-600 mt-2">Student Details</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/students')}>
            ← Back to Students
          </Button>
          <Button variant="primary" onClick={() => navigate(`/students/${studentId}/edit`)}>
            Edit Student
          </Button>
          <Button variant="danger" onClick={() => setShowDeleteDialog(true)}>
            Delete
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteStudent}
        title="Delete Student"
        message={`Are you sure you want to delete ${student.firstName} ${student.lastName}? This will permanently remove their account, stories, and progress. This action cannot be undone.`}
        confirmText="Delete Student"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Student Info */}
      <Card title="Student Information">
        <div className="p-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-600">Username</label>
            <p className="text-lg font-mono text-slate-900">{student.username || '(Not available)'}</p>
          </div>
          {student.password && (
            <div>
              <label className="text-sm font-medium text-slate-600">Password</label>
              <p className="text-lg font-mono text-slate-900">{student.password}</p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-slate-600">Classes</label>
            <p className="text-lg text-slate-900">
              {student.classes?.length || 0} class(es)
              {student.classes && student.classes.length > 0 && (
                <span className="text-sm text-slate-500 ml-2">
                  ({student.classes.join(', ')})
                </span>
              )}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">Stories Created</label>
            <p className="text-lg text-slate-900">{student.storiesCreated ?? 0}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">Stories This Week</label>
            <p className="text-lg text-slate-900">{student.weeklyStories ?? 0}</p>
          </div>
          {student.lastActive && (
            <div>
              <label className="text-sm font-medium text-slate-600">Last Active</label>
              <p className="text-lg text-slate-900">{new Date(student.lastActive).toLocaleDateString()}</p>
            </div>
          )}
        </div>
      </Card>

      {/* Parent Accounts */}
      <Card title="Parent Accounts">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-slate-600">
              Manage parent accounts that can view {student.firstName}'s stories and progress.
            </p>
            <Button
              variant="primary"
              onClick={() => navigate(`/students/${studentId}/parents`)}
            >
              Manage Parents
            </Button>
          </div>
          {student.parentEmail && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="mb-2">
                    <label className="text-sm font-medium text-slate-600">Parent Name</label>
                    <p className="text-base text-slate-900">{student.parentName || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Parent Email</label>
                    <p className="text-base text-slate-900">{student.parentEmail}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleDisconnectParent}
                  disabled={isDisconnecting}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Actions */}
      <Card title="Actions">
        <div className="p-6 space-y-3">
          <div className="flex gap-3">
            <Button 
              variant="primary" 
              onClick={handleDownloadCredentials} 
              className="flex-1"
            >
              📄 Download Credentials
            </Button>
            <Button 
              variant="outline" 
              onClick={handlePrintCredentials}
              className="flex-1"
            >
              🖨️ Print Credentials
            </Button>
          </div>
          <p className="text-sm text-slate-500 text-center">
            {student.password 
              ? 'Credentials are available for download and printing.'
              : 'Password is not available. Credentials can only be downloaded immediately after creating a student.'}
          </p>
          <div className="border-t border-slate-200 my-4"></div>
          <Button variant="outline" onClick={() => {/* TODO: Reset password */}} className="w-full">
            Reset Password
          </Button>
        </div>
      </Card>
    </div>
  );
};
